import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./style.css";
import PageTitle from "../../../layouts/PageTitle";
import ExcelDownloadButton from "../../../components/ExcelDownloadButton";
import {
  appApi,
  useGetStoreTransactionsQuery,
  useGetIssuedToOptionsQuery,
} from "../../../../store/appApi";
import { selectHasModuleAccess } from "../../../../store/authSlice";
import { updateStoreEntryApi } from "../../../../services/inventory";

const isSameDay = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";
const fmtNum = (n) => (n != null ? Number(n).toLocaleString("en-IN") : "—");

const EDIT_EMPTY = { qty: "", price: "" };
const EMPTY_FILTER = {
  entryType: "",
  dateFrom: null,
  dateTo: null,
  fromToId: "",
};

const SkuLedger = () => {
  const { skuId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasInventoryWrite = useSelector(
    selectHasModuleAccess("inventory", "read-write"),
  );

  const { data, isLoading, isFetching, isError } = useGetStoreTransactionsQuery(
    { skuId, limit: 5000 },
  );
  const { data: optionsData } = useGetIssuedToOptionsQuery();
  const issuedToOptions = Array.isArray(optionsData?.options)
    ? optionsData.options
    : [];

  const rawTransactions = Array.isArray(data?.stocks) ? data.stocks : [];
  const loading = isLoading || isFetching;

  // Recalculate balance per-SKU from oldest → newest
  const transactions = (() => {
    const sorted = [...rawTransactions].sort(
      (a, b) => new Date(a.created) - new Date(b.created),
    );
    const runningBySkuId = {};
    const withBalance = sorted.map((t) => {
      const skuKey = t.skuId?._id || String(t.skuId);
      const qty = Number(t.qty);
      const prev = runningBySkuId[skuKey] ?? 0;
      const next = t.entryType === "add" ? prev + qty : prev - qty;
      runningBySkuId[skuKey] = next;
      return { ...t, balance: next, prevBalance: prev };
    });
    return withBalance.reverse();
  })();

  const skuMeta = transactions[0]?.skuId || {};
  const currentBalance = transactions.length > 0 ? transactions[0].balance : 0;

  // ── Filter state ──────────────────────────────────────────────────────────
  const [filters, setFilters] = useState(EMPTY_FILTER);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => setFilters(EMPTY_FILTER);

  const filteredTransactions = transactions.filter((t) => {
    // Type filter
    if (filters.entryType) {
      if (filters.entryType === "add" && t.entryType !== "add") return false;
      if (filters.entryType === "issued" && t.entryType === "add") return false;
    }
    // Date from
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      from.setHours(0, 0, 0, 0);
      if (new Date(t.created) < from) return false;
    }
    // Date to
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(t.created) > to) return false;
    }
    // From / To
    if (filters.fromToId) {
      const supplierId = t.supplierId?._id || String(t.supplierId || "");
      const recipientId = String(t.recipientId || "");
      if (supplierId !== filters.fromToId && recipientId !== filters.fromToId)
        return false;
    }
    return true;
  });

  const activeFilterCount = [
    filters.entryType,
    filters.dateFrom,
    filters.dateTo,
    filters.fromToId,
  ].filter(Boolean).length;

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(EDIT_EMPTY);
  const [editLoading, setEditLoading] = useState(false);

  const canEdit = () => hasInventoryWrite;

  const openEdit = (t) => {
    setEditRow(t);
    setEditForm({ qty: t.qty ?? "", price: t.price ?? "" });
  };

  const closeEdit = () => {
    setEditRow(null);
    setEditForm(EDIT_EMPTY);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.qty || Number(editForm.qty) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid qty",
        text: "Enter a valid quantity.",
      });
      return;
    }
    try {
      setEditLoading(true);
      await updateStoreEntryApi({
        _id: editRow._id,
        qty: Number(editForm.qty),
        price: editForm.price !== "" ? Number(editForm.price) : undefined,
      });
      dispatch(appApi.util.invalidateTags(["StoreTransactions"]));
      closeEdit();
      Swal.fire({
        icon: "success",
        title: "Updated",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.response?.data?.message || "Update failed.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="SKU Ledger" motherMenu="Inventory / Store" />

      {/* ── Meta card ─────────────────────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap align-items-center gap-4">
          <div className="d-flex gap-4 flex-wrap align-items-center justify-content-center">
            <div style={{ minWidth: "120px", textAlign: "center" }}>
              <span className="text-muted small d-block">Item</span>
              <span className="fw-bold fs-5 text-primary">
                {loading ? "…" : skuMeta.skuName || "—"}
              </span>
            </div>
            <div style={{ minWidth: "120px", textAlign: "center" }}>
              <span className="text-muted d-block">Current Balance</span>
              <span className="fw-bold fs-5 text-primary">
                {loading ? "…" : fmtNum(currentBalance)}
              </span>
              <span
                className="fw-bold text-primary px-1"
                style={{ fontSize: "0.7rem" }}
              >
                {skuMeta.measurement ? `(${skuMeta.measurement})` : ""}
              </span>
            </div>
            <div style={{ minWidth: "120px", textAlign: "center" }}>
              <span className="text-muted small d-block">Total Entries</span>
              <span className="fw-bold fs-5 text-primary">
                {loading ? "…" : transactions.length}
              </span>
            </div>
          </div>
          <div className="ms-auto">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* ── Ledger table ──────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header d-flex align-items-center gap-2 flex-wrap">
          <h5 className="card-title mb-0">
            <i className="fa fa-book me-2 text-primary" />
            Transaction Ledger
          </h5>
          {!loading && (
            <span className="badge badge-primary ms-1">
              {filteredTransactions.length}
              {activeFilterCount > 0 && ` / ${transactions.length}`}
            </span>
          )}
          <ExcelDownloadButton
            endpoint="/api/stock/store/transactions/export/ledger"
            params={{
              skuId,
              entryType: filters.entryType,
              dateFrom: filters.dateFrom ? filters.dateFrom.toISOString() : undefined,
              dateTo:   filters.dateTo   ? filters.dateTo.toISOString()   : undefined,
              fromToId: filters.fromToId,
            }}
            filename={`${skuMeta.skuName || "sku"}-ledger.xlsx`}
            title={skuMeta.skuName ? `SKU Ledger — ${skuMeta.skuName}` : "SKU Ledger"}
            label="Export Excel"
            className="ms-auto"
          />
          {!hasInventoryWrite && (
            <span className="small text-muted">
              <i className="fa fa-info-circle me-1" />
              Read-only access. Contact admin to enable editing.
            </span>
          )}
        </div>

        {/* ── Filter Bar ── */}
        <div className="card-body pb-2 pt-2 border-bottom">
          <div className="row g-2 align-items-end">
            {/* Type */}
            <div className="col-sm-6 col-md-2 col-xl-2">
              <label className="form-label small mb-1">Type</label>
              <select
                name="entryType"
                className="form-control form-control-sm"
                value={filters.entryType}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="add">Received</option>
                <option value="issued">Issued</option>
              </select>
            </div>

            {/* Date From */}
            <div className="col-sm-6 col-md-2 col-xl-2">
              <label className="form-label small mb-1">Date From</label>
              <DatePicker
                selected={filters.dateFrom}
                onChange={(date) =>
                  setFilters((prev) => ({ ...prev, dateFrom: date }))
                }
                selectsStart
                startDate={filters.dateFrom}
                endDate={filters.dateTo}
                maxDate={filters.dateTo || new Date()}
                dateFormat="dd MMM yyyy"
                placeholderText="From date"
                isClearable
                className="form-control form-control-sm"
                wrapperClassName="w-100"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>

            {/* Date To */}
            <div className="col-sm-6 col-md-2 col-xl-2">
              <label className="form-label small mb-1">Date To</label>
              <DatePicker
                selected={filters.dateTo}
                onChange={(date) =>
                  setFilters((prev) => ({ ...prev, dateTo: date }))
                }
                selectsEnd
                startDate={filters.dateFrom}
                endDate={filters.dateTo}
                minDate={filters.dateFrom}
                maxDate={new Date()}
                dateFormat="dd MMM yyyy"
                placeholderText="To date"
                isClearable
                className="form-control form-control-sm"
                wrapperClassName="w-100"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>

            {/* From / To */}
            <div className="col-sm-6 col-md-3 col-xl-3">
              <label className="form-label small mb-1">From / To</label>
              <select
                name="fromToId"
                className="form-control form-control-sm"
                value={filters.fromToId}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {issuedToOptions.length > 0 && (
                  <>
                    <optgroup label="Suppliers">
                      {issuedToOptions
                        .filter((o) => o.type === "supplier")
                        .map((o) => (
                          <option key={o._id} value={o._id}>
                            {o.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Contractors">
                      {issuedToOptions
                        .filter((o) => o.type === "contractor")
                        .map((o) => (
                          <option key={o._id} value={o._id}>
                            {o.name}
                          </option>
                        ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {/* Clear */}
            <div className="col-sm-auto col-md-auto">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={clearFilters}
                disabled={!activeFilterCount}
              >
                <i className="fa fa-times me-1" />
                Clear
                {activeFilterCount > 0 && (
                  <span className="badge badge-danger ms-1">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          {isError && (
            <div className="alert alert-danger m-3">
              Failed to load transactions. Please try again.
            </div>
          )}
          <div className="table-responsive">
            <table className="display dataTable no-footer w-100">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Vendor / Contractor</th>
                  <th>Bill No / V. No</th>
                  <th>Open Qty</th>
                  <th>Receipts Qty</th>
                  <th>Issued Qty</th>
                  <th>Balance Qty</th>
                  <th>Truck No</th>
                  <th>Location / Remarks</th>
                  <th>Issued By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={13} className="text-center py-5">
                      <span className="spinner-border spinner-border-sm me-2" />
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && filteredTransactions.length === 0 && !isError && (
                  <tr>
                    <td colSpan={13} className="text-center py-5 text-muted">
                      {activeFilterCount > 0
                        ? "No transactions match the selected filters."
                        : "No transactions found for this item."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  filteredTransactions.map((t, idx) => {
                    const editAllowed = canEdit(t);
                    const fromTo =
                      t.entryType === "add"
                        ? t.supplierId?.name || "—"
                        : t.recipientName || "—";
                    return (
                      <tr key={t._id}>
                        <td className="text-muted small">
                          {filteredTransactions.length - idx}
                        </td>
                        <td className="small">{fmtDate(t.created)}</td>
                        <td>
                          <span
                            className={`badge ${
                              t.entryType === "add"
                                ? "badge-success"
                                : "badge-warning"
                            }`}
                          >
                            {t.entryType === "add" ? "Received" : "Issued"}
                          </span>
                          {t.isUsedInVehicle && (
                            <span
                              className="badge badge-info ms-1"
                              title={`Vehicle: ${t.vehicleId?.vehicleNumber || ""}`}
                            >
                              <i className="fa fa-car" />
                            </span>
                          )}
                        </td>
                        <td className="small">{fromTo}</td>
                        <td className="small text-muted">
                          {t.invoiceNo || "—"}
                        </td>
                        <td className="small text-muted">
                          {fmtNum(t.prevBalance)}
                        </td>
                        <td className="small fw-semibold text-success">
                          {t.entryType === "add" ? fmtNum(t.qty) : ""}
                        </td>
                        <td className="small fw-semibold text-danger">
                          {t.entryType === "add" ? "" : fmtNum(t.qty)}
                        </td>
                        <td className="small fw-semibold">
                          {fmtNum(t.balance)}
                        </td>
                        <td className="small text-muted">
                          {t.truckNo || "—"}
                        </td>
                        <td className="small text-muted">
                          {[t.location, t.remarks].filter(Boolean).join(" · ") || "—"}
                        </td>
                        <td className="small text-muted">
                          {t.userId?.name || "—"}
                        </td>
                        <td>
                          <button
                            className="btn btn-xs btn-primary"
                            onClick={() => openEdit(t)}
                            disabled={!editAllowed}
                            title={
                              editAllowed
                                ? "Edit entry"
                                : "Past entry — super-admin required"
                            }
                          >
                            <i className="fa fa-pencil" />
                          </button>
                          {!editAllowed && (
                            <div
                              className="small text-muted mt-1"
                              style={{ fontSize: "0.65rem" }}
                            >
                              Super-admin only
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Edit Modal ────────────────────────────────────────────────────── */}
      {editRow && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Edit Entry
                  {!isSameDay(editRow.created) && (
                    <span className="badge badge-warning ms-2 small">
                      Past Entry Edit
                    </span>
                  )}
                </h5>
                <button className="btn-close" onClick={closeEdit} />
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="mb-2 small text-muted">
                    <strong>Date:</strong> {fmtDate(editRow.created)}
                    &nbsp;|&nbsp;
                    <strong>Type:</strong>{" "}
                    <span
                      className={`badge ${
                        editRow.entryType === "add"
                          ? "badge-success"
                          : "badge-warning"
                      }`}
                    >
                      {editRow.entryType === "add" ? "Received" : "Issued"}
                    </span>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Quantity</label>
                    <input
                      type="number"
                      name="qty"
                      className="form-control"
                      value={editForm.qty}
                      onChange={handleEditChange}
                      min={1}
                      required
                    />
                  </div>
                  {editRow.entryType === "add" && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Total Price
                      </label>
                      <input
                        type="number"
                        name="price"
                        className="form-control"
                        value={editForm.price}
                        onChange={handleEditChange}
                        min={0}
                      />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={editLoading}
                  >
                    {editLoading && (
                      <span className="spinner-border spinner-border-sm me-1" />
                    )}
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SkuLedger;
